import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { getData } from "features/dinamic";
import { setLoading } from "features/loader";
import { setDataToast, setVisibleToast } from "features/toast/toastBaseSlice";

interface SolicitudDetalle {
    idSolicitud: number | null;
    idAlumno: number | null;
    idDiplomado: number | null;
    idCicloDeseado: number | null;
    tipo: string;
    estado: string;
    motivoRechazo: string | null;
    fechaSolicitud: string | null;
    fechaDecision: string | null;
    idUsuarioAdmin: number | null;
    fechaCreacion: string | null;
    usuarioCreacion: string | null;
    fechaModificacion: string | null;
    usuarioModificacion: string | null;
    infoAlumno: {
        usuario: string;
        estadoUsuario: boolean;
        primerNombre: string;
        segundoNombre: string | null;
        tercerNombre: string | null;
        primerApellido: string;
        segundoApellido: string | null;
        cui: string | null;
        idGenero: number | null;
        fechaNacimiento: string | null;
        idOcupacion: number | null;
        detalleOcupacion: string | null;
        idDepartamento: number | null;
        idMunicipio: number | null;
        direccion: string | null;
        zona: string | null;
        correo: string | null;
        telefonoPrincipal: string | null;
        telefonoSecundario: string | null;
        idGrado: number | null;
        idPromedio: number | null;
        idTipoCentro: number | null;
        nombreCentroEducativo: string | null;
    } | null;
    infoAdicional: {
        idRelacion: number | null;
        motivoRelacion: string | null;
        idActividadExtra: number | null;
        motivoActividadExtra: string | null;
    } | null;
    idiomas: Array<{ idIdioma: number }>;
    encargados: Array<{
        primerNombre: string;
        segundoNombre: string | null;
        primerApellido: string;
        segundoApellido: string | null;
        cui: string | null;
        correo: string | null;
        telefonoPrincipal: string | null;
        telefonoSecundario: string | null;
        idParentesco: number | null;
        idOcupacion: number | null;
    }>;
    infoDiplomado: {
        nombreDiplomado: string;
        descripcion: string;
        estadoDiplomado: boolean;
    } | null;
    infoAdmin: {
        usuarioAdmin: string;
        fechaDecision: string | null;
        motivoRechazo: string | null;
    } | null;
}

interface SolicitudState {
    status: string;
    error: string;
    solicitudList: any[];
    solicitud: any;
    solicitudDetalle: SolicitudDetalle | null;
    loadingDetalle: boolean;
    filters: {
        tipo: string;
        estado: string;
        idDiplomado: number | null;
        q: string;
        page: number;
        pageSize: number;
    };
    totalPages: number;
    totalRecords: number;
}

const initialState: SolicitudState = {
    status: 'idle',
    error: '',
    solicitudList: [],
    solicitud: {
        idSolicitud: null,
        tipo: '',
        estado: '',
        fechaSolicitud: null,
        fechaDecision: null,
        idUsuario: null,
        idDiplomado: null,
        idCicloDeseado: null,
        motivoRechazo: null,
        usuario: '',
        nombreDiplomado: '',
        nombreCicloDeseado: null
    },
    solicitudDetalle: null,
    loadingDetalle: false,
    filters: {
        tipo: '',
        estado: 'PENDIENTE',
        idDiplomado: null,
        q: '',
        page: 1,
        pageSize: 10
    },
    totalPages: 0,
    totalRecords: 0
};

// Obtener lista de solicitudes con filtros
export const fetchSolicitudes = createAsyncThunk(
    'solicitudAdmin/fetchSolicitudes',
    async (params: Partial<SolicitudState['filters']>, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const currentFilters = state.solicitudAdmin.filters;

        const filters = { ...currentFilters, ...params };

        const body = {
            idsp: 305, // PA_AD_SOLICITUD_READ
            tipo: filters.tipo || undefined,
            estado: filters.estado || undefined,
            idDiplomado: filters.idDiplomado || undefined,
            q: filters.q || '',
            page: filters.page,
            pageSize: filters.pageSize
        };

        dispatch(setLoading(true));
        const { result, message, data } = await getData(body, getState, dispatch);
        dispatch(setLoading(false));

        if (result && Array.isArray(data)) {
            // Transformar datos para la tabla
            const transformedData = data.map((item: any) => {
                const usuario = item.u?.[0]?.usuario || 'Sin usuario';
                const diplomado = item.u?.[0]?.d?.[0]?.nombreDiplomado || 'Sin diplomado';
                const ciclo = item.u?.[0]?.d?.[0]?.c?.[0]?.nombreCicloDeseado || null;
                const total = item.u?.[0]?.d?.[0]?.c?.[0]?.total || 0;

                return {
                    ...item,
                    usuario,
                    nombreDiplomado: diplomado,
                    nombreCicloDeseado: ciclo,
                    total
                };
            });

            return {
                solicitudes: transformedData,
                filters,
                totalRecords: transformedData.length > 0 ? transformedData[0].total || transformedData.length : 0
            };
        }

        return rejectWithValue(message || 'Error al obtener solicitudes');
    }
);

// Aprobar solicitud
export const aprobarSolicitud = createAsyncThunk(
    'solicitudAdmin/aprobarSolicitud',
    async ({ idSolicitud, idCiclo }: { idSolicitud: number; idCiclo: number }, { dispatch, getState, rejectWithValue }) => {
        const body = {
            idsp: 306, // PA_AD_SOLICITUD_APPROVE
            idSolicitud,
            idCiclo
        };

        dispatch(setLoading(true));
        const { result, message } = await getData(body, getState, dispatch);
        dispatch(setLoading(false));

        if (result) {
            dispatch(setDataToast({
                title: "Solicitud Aprobada",
                message: message || "La solicitud ha sido aprobada exitosamente",
                data: [],
                type: "LimeGreen",
                time: Date.now()
            }));
            dispatch(setVisibleToast(true));

            return { idSolicitud, status: 'APROBADA' };
        }

        dispatch(setDataToast({
            title: "Error al aprobar solicitud",
            message: message || "No fue posible aprobar la solicitud",
            data: [],
            type: "Crimson",
            time: Date.now()
        }));
        dispatch(setVisibleToast(true));

        return rejectWithValue(message || 'Error al aprobar solicitud');
    }
);

// Rechazar solicitud
export const rechazarSolicitud = createAsyncThunk(
    'solicitudAdmin/rechazarSolicitud',
    async ({ idSolicitud, motivo }: { idSolicitud: number; motivo: string }, { dispatch, getState, rejectWithValue }) => {
        const body = {
            idsp: 307, // PA_AD_SOLICITUD_REJECT
            idSolicitud,
            motivo
        };

        dispatch(setLoading(true));
        const { result, message } = await getData(body, getState, dispatch);
        dispatch(setLoading(false));

        if (result) {
            dispatch(setDataToast({
                title: "Solicitud Rechazada",
                message: message || "La solicitud ha sido rechazada",
                data: [],
                type: "Orange",
                time: Date.now()
            }));
            dispatch(setVisibleToast(true));

            return { idSolicitud, status: 'RECHAZADA', motivo };
        }

        dispatch(setDataToast({
            title: "Error al rechazar solicitud",
            message: message || "No fue posible rechazar la solicitud",
            data: [],
            type: "Crimson",
            time: Date.now()
        }));
        dispatch(setVisibleToast(true));

        return rejectWithValue(message || 'Error al rechazar solicitud');
    }
);

export const fetchSolicitudDetalle = createAsyncThunk(
    'solicitudAdmin/fetchSolicitudDetalle',
    async (idSolicitud: number, { dispatch, getState, rejectWithValue }) => {
        const body = {
            idsp: 308,
            idSolicitud
        };

        dispatch(setLoading(true));
        const { result, message, data } = await getData(body, getState, dispatch);
        dispatch(setLoading(false));

        if (result && Array.isArray(data) && data.length > 0) {
            return data[0] as SolicitudDetalle;
        }

        dispatch(setDataToast({
            title: "Error al cargar detalle",
            message: message || "No se pudo obtener el detalle de la solicitud",
            data: [],
            type: "Crimson",
            time: Date.now()
        }));
        dispatch(setVisibleToast(true));

        return rejectWithValue(message || 'Error al obtener detalle de solicitud');
    }
);

const solicitudSlice = createSlice({
    name: 'solicitudAdmin',
    initialState,
    reducers: {
        setSolicitud: (state, action) => {
            state.solicitud = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetFilters: (state) => {
            state.filters = initialState.filters;
        },
        clearSolicitudState: (state) => {
            state.solicitud = initialState.solicitud;
            state.error = '';
        },
        clearSolicitudDetalle: (state) => {
            state.solicitudDetalle = null;
            state.loadingDetalle = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch solicitudes
            .addCase(fetchSolicitudes.pending, (state) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(fetchSolicitudes.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.solicitudList = action.payload.solicitudes;
                state.filters = action.payload.filters;
                state.totalRecords = action.payload.totalRecords;
                state.totalPages = Math.ceil(action.payload.totalRecords / state.filters.pageSize);
            })
            .addCase(fetchSolicitudes.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            // Aprobar solicitud
            .addCase(aprobarSolicitud.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(aprobarSolicitud.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Actualizar la solicitud en la lista
                const index = state.solicitudList.findIndex(s => s.idSolicitud === action.payload.idSolicitud);
                if (index !== -1) {
                    state.solicitudList[index].estado = 'APROBADA';
                    state.solicitudList[index].fechaDecision = new Date().toISOString();
                }
            })
            .addCase(aprobarSolicitud.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            // Rechazar solicitud
            .addCase(rechazarSolicitud.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(rechazarSolicitud.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Actualizar la solicitud en la lista
                const index = state.solicitudList.findIndex(s => s.idSolicitud === action.payload.idSolicitud);
                if (index !== -1) {
                    state.solicitudList[index].estado = 'RECHAZADA';
                    state.solicitudList[index].fechaDecision = new Date().toISOString();
                    state.solicitudList[index].motivoRechazo = action.payload.motivo;
                }
            })
            .addCase(rechazarSolicitud.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            .addCase(fetchSolicitudDetalle.pending, (state) => {
                state.loadingDetalle = true;
                state.solicitudDetalle = null;
            })
            .addCase(fetchSolicitudDetalle.fulfilled, (state, action) => {
                state.loadingDetalle = false;
                state.solicitudDetalle = action.payload;
            })
            .addCase(fetchSolicitudDetalle.rejected, (state, action) => {
                state.loadingDetalle = false;
                state.error = action.payload as string;
            });
    }
});

export const { setSolicitud, setFilters, resetFilters, clearSolicitudState, clearSolicitudDetalle } = solicitudSlice.actions;

export const selectSolicitudList = (state: RootState) => state.solicitudAdmin.solicitudList;
export const selectSolicitud = (state: RootState) => state.solicitudAdmin.solicitud;
export const selectSolicitudStatus = (state: RootState) => state.solicitudAdmin.status;
export const selectSolicitudError = (state: RootState) => state.solicitudAdmin.error;
export const selectSolicitudFilters = (state: RootState) => state.solicitudAdmin.filters;
export const selectSolicitudTotalPages = (state: RootState) => state.solicitudAdmin.totalPages;
export const selectSolicitudTotalRecords = (state: RootState) => state.solicitudAdmin.totalRecords;
export const selectSolicitudDetalle = (state: RootState) => state.solicitudAdmin.solicitudDetalle;
export const selectLoadingDetalle = (state: RootState) => state.solicitudAdmin.loadingDetalle;

export default solicitudSlice.reducer;